import * as yup from 'yup';

import { Add as AddIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, Avatar, Box, Button, Container, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import MuiAutoCompleteCountry from "../../components/InputControls/MuiAutoCompleteCountry";
import MuiPhoneNumber from "mui-phone-input";
import api from "../../api/axiosInstance";
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

const VALIDATION_PATTERNS = {
  PHONE: /^(\+[1-9]{1}[0-9]{3,})*((00|\+)[1-9]{1}[0-9]{1,2})?[\s\.\-\(\)\d]+$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
};

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];

const FORM_DEFAULTS = {
  firstName: '',
  lastName: '',
  secondaryEmail: '',
  phoneNumber: '',
  gender: '',
  dateOfBirth: '',
  country: undefined
};

const FORM_STORAGE_KEY = 'personalDetailsForm';

// Country field needs specific styling for the autocomplete
const COUNTRY_FIELD_STYLES = {
  '& .MuiAutocomplete-root': {
    '& .MuiOutlinedInput-root': {
      minHeight: '40px !important',
      height: '40px !important',
      '& .MuiAutocomplete-input': {
        padding: '8.5px 14px !important',
      }
    },
    '& .MuiInputLabel-root': {
      transform: 'translate(14px, 9px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      }
    }
  },
  '& input': {
    height: '40px !important',
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-root': {
    height: '40px !important',
  }
};

const personalDetailsSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  secondaryEmail: yup
    .string()
    .matches(VALIDATION_PATTERNS.EMAIL, 'Please enter a valid email address')
    .nullable()
    .transform((value) => value === '' ? null : value),
  phoneNumber: yup
    .string()
    .matches(VALIDATION_PATTERNS.PHONE, 'Phone number is not valid')
    .required('Phone number is required')
    .transform((value) => {
      if (typeof value === 'object' && value !== null) {
        return `+${value.countryCode}${value.areaCode?.replace(/"/g, '') || ''}${value.phoneNumber?.replace(/"/g, '') || ''}`;
      }
      return value;
    }),
  gender: yup.string().required('Gender is required'),
  dateOfBirth: yup
    .string()
    .test(
      'age-validation',
      'You must be 18 years or older',
      function (value) {
        if (!value) return false;
        const today = new Date();
        const birthDate = new Date(value);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          return age - 1 >= 18;
        }
        return age >= 18;
      }
    )
    .required('Date of birth is required'),
  country: yup
    .object()
    .shape({
      code: yup.string().required(),
      label: yup.string().required(),
    })
    .nullable()
    .required("Country is required")
});

const phoneUtils = {
  formatPhoneNumber: (phoneData) => {
    if (typeof phoneData === 'object' && phoneData !== null) {
      return `+${phoneData.countryCode}${phoneData.areaCode?.replace(/"/g, '') || ''}${phoneData.phoneNumber?.replace(/"/g, '') || ''}`;
    }
    return phoneData;
  }
};

// LocalStorage utilities for form persistence
const storageUtils = {
  saveFormData: (data) => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save form data:', error);
    }
  },
  
  loadFormData: () => {
    try {
      const saved = localStorage.getItem(FORM_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Failed to load form data:', error);
      return null;
    }
  },
  
  clearFormData: () => {
    try {
      localStorage.removeItem(FORM_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear form data:', error);
    }
  }
};

const usePersonalDetailsForm = (formData) => {
  // Load saved data from localStorage on initial load
  const savedData = storageUtils.loadFormData();
  
  const initialData = {
    ...FORM_DEFAULTS,
    ...formData,
    ...savedData // Restored data takes priority
  };

  return useForm({
    resolver: yupResolver(personalDetailsSchema),
    mode: 'onChange',
    defaultValues: initialData
  });
};

const apiService = {
  savePersonalDetails: async (data) => {
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    
    if (!serverUrl) {
      throw new Error('API configuration is missing. Please contact support.');
    }

    const phoneNumber = phoneUtils.formatPhoneNumber(data.phoneNumber);
    const body = {
      ...data,
      phoneNumber,
      country: data.country ? JSON.stringify(data.country) : undefined,
    };
    
    try {
      const response = await api.patch(
        "/users/me",
        body, 
        { headers: { "Content-Type": "application/json",} }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'An unexpected error occurred. Please try again.');
    }
  }
};

const FormHeader = () => (
  <Box sx={{ textAlign: 'center', mb: 3 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>
      Personal Details
    </Typography>
    <Typography variant="body2">
      Let's get to know you more.
    </Typography>
  </Box>
);

const ProfilePhoto = ({ firstName, lastName }) => {
  const initials = useMemo(() => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return 'OP';
  }, [firstName, lastName]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar 
          alt="Avatar" 
          sx={{ 
            width: 96, 
            height: 96, 
            bgcolor: 'grey.400',
            fontSize: '32px',
            fontWeight: 'bold'
          }}
        >
          {initials}
        </Avatar>
        <IconButton
          sx={{
            position: 'absolute',
            bottom: 5,
            right: 5,
            bgcolor: 'white',
            border: 1,
            borderColor: 'grey.300',
            width: 32,
            height: 32,
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'grey.50',
              transform: 'scale(1.05)'
            }
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

const NameFields = ({ control, errors }) => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Controller
      name="firstName"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          label="First Name"
          placeholder="Jane"
          required
          size="small"
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
        />
      )}
    />
    <Controller
      name="lastName"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          label="Last Name"
          placeholder="Doe"
          required
          size="small"
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
        />
      )}
    />
  </Box>
);

const EmailFields = ({ control, errors }) => (
  <Controller
    name="secondaryEmail"
    control={control}
    render={({ field }) => (
      <TextField
        {...field}
        fullWidth
        label="Secondary Email"
        type="email"
        placeholder="example@gmail.com"
        size="small"
        error={!!errors.secondaryEmail}
        helperText={errors.secondaryEmail?.message}
      />
    )}
  />
);

const PhoneNumberField = ({ control, errors }) => {
  const handlePhoneChange = useCallback((onChange) => (phoneData) => {
    if (typeof phoneData === 'object' && phoneData !== null) {
      const formattedPhone = phoneUtils.formatPhoneNumber(phoneData);
      onChange(formattedPhone);
    } else {
      onChange(phoneData);
    }
  }, []);

  return (
    <Controller
      name="phoneNumber"
      control={control}
      defaultValue=""
      rules={{ required: true }} 
      render={({ field: { onChange, value } }) => (
        <MuiPhoneNumber
          label="Phone Number"
          defaultCountry="ke"
          regions={["africa", "europe"]}
          onChange={handlePhoneChange(onChange)}
          value={value}
          fullWidth
          size="small"
          error={!!errors.phoneNumber}
          helperText={errors?.phoneNumber?.message || ""}
        />
      )}
    />
  );
};

const GenderAndDateFields = ({ control, errors }) => {
  const getCurrentDate = () => new Date().toISOString().split('T')[0];
  const getMinDate = () => {
    const today = new Date();
    const minDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
    return minDate.toISOString().split('T')[0];
  };

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Controller
        name="gender"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size="small" error={!!errors.gender}>
            <InputLabel>Gender</InputLabel>
            <Select {...field} label="Gender">
              <MenuItem value=""><em>Select</em></MenuItem>
              {GENDERS.map((gender) => (
                <MenuItem key={gender} value={gender}>{gender}</MenuItem>
              ))}
            </Select>
            {errors.gender && (
              <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                {errors.gender.message}
              </Typography>
            )}
          </FormControl>
        )}
      />

      <Controller
        name="dateOfBirth"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Date of Birth"
            type="date"
            size="small"
            required
            error={!!errors.dateOfBirth}
            helperText={errors.dateOfBirth?.message}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: getCurrentDate(), 
              min: getMinDate(),
            }}
          />
        )}
      />
    </Box>
  );
};

const CountryField = ({ control, errors }) => (
  <Box sx={COUNTRY_FIELD_STYLES}>
    <MuiAutoCompleteCountry control={control} errors={errors} />
  </Box>
);

const FormActions = ({ onBack, isValid, isSubmitting, isFirstStep }) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: isFirstStep ? 'flex-end' : 'space-between', 
    mt: 1
  }}>
    {!isFirstStep && (
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        disabled={isSubmitting}
        size="small"
      >
        Back
      </Button>
    )}

    <Button
      type="submit"
      variant="contained"
      disabled={!isValid || isSubmitting}
      size="small"
      sx={{ px: 3 }}
    >
      {isSubmitting ? 'Saving...' : 'Next'}
    </Button>
  </Box>
);

const StatusAlert = ({ saveStatus, onClose }) => {
  if (!saveStatus) return null;

  return (
    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
      <Alert 
        severity={saveStatus.type} 
        sx={{ width: '512px' }}
        onClose={onClose}
        action={
          saveStatus.type === 'error' && saveStatus.retry ? (
            <Button color="inherit" size="small" onClick={saveStatus.retry}>
              Retry
            </Button>
          ) : null
        }
      >
        {saveStatus.message}
      </Alert>
    </Box>
  );
};

const PersonalDetailsForm = ({ 
  formData, 
  setFormData, 
  onNext, 
  onBack, 
  isFirstStep = false 
}) => {
  const navigate = useNavigate();
  const [saveStatus, setSaveStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { control, handleSubmit, watch, formState: { errors, isValid } } = usePersonalDetailsForm(formData);

  // Auto-save form data to localStorage as user types
  useEffect(() => {
    const subscription = watch((watchedValues) => {
      const cleanedValues = Object.keys(watchedValues).reduce((acc, key) => {
        if (watchedValues[key] !== undefined) {
          acc[key] = watchedValues[key];
        }
        return acc;
      }, {});

      storageUtils.saveFormData(cleanedValues);
      setFormData(prev => ({ ...prev, ...cleanedValues }));
    });

    return () => subscription.unsubscribe();
  }, [watch, setFormData]);

  const onSubmit = useCallback(async (data) => {
    setIsSubmitting(true);
    setSaveStatus(null);
    
    try {
      await apiService.savePersonalDetails(data);
      setFormData(prev => ({ ...prev, ...data }));
      
      // Clear localStorage after successful submission
      storageUtils.clearFormData();
      
      setTimeout(() => onNext(), 0);
    } catch (error) {
      if (error.message.includes('session has expired') || error.message.includes('log in again')) {
        setSaveStatus({ type: 'error', message: error.message });
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      setSaveStatus({ 
        type: 'error', 
        message: error.message || 'Failed to save personal details. Please try again.',
        retry: () => {
          setSaveStatus(null);
          handleSubmit(onSubmit)();
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [setFormData, onNext, navigate, handleSubmit]);

  const watchedValues = watch();

  return (
    <Box sx={{ 
      height: 'calc(100vh - 116px)',
      bgcolor: '#EAE7E6',
      pt: 2,
      px: '80px'
    }}>
      <Container maxWidth={false} sx={{ px: 0, height: '100%' }}>
        <Box sx={{ 
          p: 4, 
          bgcolor: 'white',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <FormHeader />
          <ProfilePhoto firstName={watchedValues.firstName} lastName={watchedValues.lastName} />
          <StatusAlert saveStatus={saveStatus} onClose={() => setSaveStatus(null)} />

          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Box sx={{ width: '512px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <NameFields control={control} errors={errors} />
                <EmailFields control={control} errors={errors} />
                <PhoneNumberField control={control} errors={errors} />
                <GenderAndDateFields control={control} errors={errors} />
                <CountryField control={control} errors={errors} />
                <FormActions onBack={onBack} isValid={isValid} isSubmitting={isSubmitting} isFirstStep={isFirstStep} />
              </form>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PersonalDetailsForm;